import { values } from 'ramda'
import { useCallback, useRef, useEffect, type RefCallback } from 'react'

const COPY_CLASS_BASE = 'copy_' as const
const extractIdFromClassName = (className: string): string | null => {
  const res = className.match(new RegExp(`${COPY_CLASS_BASE}(\\S+)`))
  return res ? res[1] : null
}
export const createWidthCopyClassName = (id: string) =>
  `${COPY_CLASS_BASE}${id.replace(/\s/g, '_')}`

/**
 *  Hook, копирующий ширину ячеек строк таблицы в другую такую же таблицу (или таблицы)
 *  Соответствующая ячейка находится по селектору `.{copy_${id}}`
 *
 *  Пример:
 *    const [refTrFrom, refTrTo] = useCopyCellsOfTr()
 *    ...
 *    <Table>
 *      <Tr header reference={refTrTo}>
 *        <Th class={`{copy_${id}`} ...
 *    ...
 *    <Table>
 *      <Tr header hidden reference={refTrFrom}>
 *        <Th class={`{copy_${id}`} ...
 *    ...
 */

export function useCopyCellsOfTr(updateKey: any = null) {
  const firstUpdate = useRef(true)

  // цели копирования, ячейки таблицы th или td по идентификатору
  const refTargets = useRef<Record<string, HTMLElement>>({})
  // table rows - источники копирования, tr содержащие ячейки таблицы th или td
  const refFrom = useRef<Array<HTMLElement>>([])
  // table rows - цели копирования, tr содержащие ячейки таблицы th или td
  const refTo = useRef<Array<HTMLElement>>([])

  const ro = useRef(
    new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
      // const compensateBorder: Array<number> = []
      entries.forEach(({ contentRect, target: sourceElem }) => {
        const { width } = contentRect
        const targetId = extractIdFromClassName(sourceElem.className)
        if (!targetId) {
          console.error(
            `Не удалось определить id селектора для копирования, установите класс "copy_${targetId}" у ячеек для копирования (source)`,
          )
          return
        }
        const targetElem = refTargets.current[targetId]
        if (!targetElem) {
          return
        }
        const targetStyle = window.getComputedStyle(targetElem, null)
        const padding =
          parseInt(targetStyle.getPropertyValue('padding-left'), 10) +
          parseInt(targetStyle.getPropertyValue('padding-right'), 10)
        // const borderRight = parseInt(targetStyle.getPropertyValue('border-right-width'), 10)
        // compensateBorder[index + 1] = borderRight;
        window.requestAnimationFrame(() => {
          targetElem.style.width = `${Math.round(width) + padding /* + (compensateBorder[index] || 0) */}px`
        })
      })
    }),
  )

  const refTrFrom: RefCallback<HTMLTableRowElement> = useCallback((node) => {
    if (!node) return
    refFrom.current.push(node)
    const sourceCells = node.children

    Array.from(sourceCells).forEach((elem: Element) => {
      ro.current.unobserve(elem)
      ro.current.observe(elem)
    })
  }, [])

  const refTrTo = useCallback((node: HTMLElement | null) => {
    if (!node) return
    refTo.current.push(node)
    const targetCells = node.children
    Array.from(targetCells).forEach((elem: Element) => {
      const targetId = extractIdFromClassName(elem.className)
      if (!targetId) {
        console.error(
          `Не удалось определить id селектора для копирования, установите класс "copy_${targetId}" у ячеек для копирования (target)`,
        )
        return
      }
      refTargets.current[targetId] = elem as HTMLElement
    })
  }, [])

  useEffect(() => {
    // переустановка обсервера
    // не делаем это при первой перерисовке
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }
    const sourceRowNodes = refFrom.current
    const targetRowNodes = refTo.current
    // const targetCellNodes = refTargets.current

    // отключаем обсервер
    ro.current.disconnect()

    // убираем назначенные стили ширины у целевых элементов
    values(refTargets.current).forEach((element) => {
      element.style.width = '' // eslint-disable-line no-param-reassign
    })

    // закидываем все строки таблицы в обсервер снова
    refTo.current = []
    targetRowNodes.forEach((element: HTMLElement) => {
      refTrTo(element as HTMLTableRowElement)
    })
    refFrom.current = []
    sourceRowNodes.forEach((element: HTMLElement) => {
      refTrFrom(element as HTMLTableRowElement)
    })
  }, [updateKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return [refTrFrom, refTrTo]
}
